import { gql } from '@apollo/client';

import client from '../../../core/services/graphql';

export const LIMIT = 50;

export const GET_TEST_SESSIONS = gql`
  query TestSessionsApprovalsTab(
    $status: TestSessionStatusEnum,
    $projectId: ID!,
    $limit: Int,
  ) {
    testSessionsApprovalsTab(
      status: $status,
      projectId: $projectId,
      limit: $limit,
    ) {
      id
      approved
      removed
      tester {
        id
        approved
        removedReason
        cintId
        mrId
        userInterviewsParticipantId
        userInterviewsStatus
        userInterviewsPayStatus
        screenerSession {
          id
        }
        segment {
          name
          createdAt
        }
        user {
          id
          tester
          guest
          profile {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const APPROVE_TESTER = gql`
  mutation ApproveTester($id: ID!) {
    approveTester(id: $id) {
      tester {
        id
      }
    }
  }
`;

export const REMOVE_TESTER = gql`
  mutation RemoveTester($id: ID!) {
    removeTester(id: $id) {
      tester {
        id
        removedReason
      }
    }
  }
`;

export const GET_TESTERS = gql`
  query Testers(
    $approved: Boolean,
    $bookingStatus: [TestSessionBookingStatusEnum!],
    $status: TesterStatusEnum,
    $removed: Boolean,
    $projectId: ID!,
    $isPending: Boolean,
    $isCompleted: Boolean,
    $isExpired: Boolean,
    $limit: Int,
    $offset: Int,
    $isModerated: Boolean,
  ) {
    testers(
      approved: $approved,
      bookingStatus: $bookingStatus,
      status: $status,
      removed: $removed,
      projectId: $projectId,
      isPending: $isPending,
      isCompleted: $isCompleted,
      isExpired: $isExpired,
      limit: $limit,
      offset: $offset,
      isModerated: $isModerated
    ) {
      id
      status
      testSessions {
        id
        completedAt
        expiresAt
        expired
        status
        videoUrl
        convertedVideoKey
        bookingStatus
        didNotShowAction
      }
      user {
        id
        email
        profile {
          id
          firstName
          lastName
          linkedinFields {
            id
            linkedinUrl
          }
          paymentMethod
          paypalAccount
          venmoAccount
        }
      }
      project {
        id
        name
        description
        createdAt
        company {
          id
          name
        }
        createdBy {
          id
          profile {
            id
            firstName
            lastName
          }
        }
        segments {
          id
          name
          audienceParticipants
        }
      }
    }
  }
`;

export const GET_APPROVALS = gql`
  query Approvals($limit: Int!, $offset: Int!, $participantSource: [ProjectParticipantsSourceEnum!]) {
    approvals(limit: $limit, offset: $offset, participantSource: $participantSource) {
      project {
        id
        status
        createdAt
        launchedOn
        updatedAt
        name
        description
        testType
        studyStatus
        releasedAt
        thankYouRedirectLink
        disqualifyRedirectUrl
        elasticsearchProjectStatus
        indexedOn
        audienceParticipants
        submittedAvailabilities
        meetingId
        receivedTests
        participantsSource
        sessionDuration
        faceRecording
        hasMrId
        segments {
          id
          name
          recorderType
          audienceParticipants
          device
          userInterviewsProjectId
          userInterviewsProjectUrl
          userInterviewsProjectCreatedAt
          userInterviewsProjectLaunch
          userInterviewsProjectLaunchAt
          recruitParticipantTokens {
            token
            deletedAt
            maximumTests
          }
        }
        company {
          id
          name
        }
        createdBy {
          id
          profile {
            id
            firstName
            lastName
          }
        }
        testers {
          id
          status
        }
      }
      approvalTypes
    }
  }
`;

export const DISMISS_APPROVAL = gql`
  mutation DismissApproval($projectId: ID!, $approvalType: ApprovalTypeEnum!) {
    dismissApproval(projectId: $projectId, approvalType: $approvalType) {
      projectId
      approvalType
    }
  }
`;

export const UNDISMISS_APPROVAL = gql`
  mutation UndismissApproval($projectId: ID!) {
    undismissApproval(projectId: $projectId) {
      project {
        id
      }
    }
  }
`;

export const MARK_PROJECT_AS_DONE = gql`
  mutation MarkProjectAsDone ($projectId: ID!) {
    markProjectAsDone(projectId: $projectId) {
      projectId
    }
  }
`;

export const GET_RECRUITING_LINKS = gql`
  query fetchRecruitingLinks($projectId: ID!) {
    project(id: $projectId) {
      segments {
        id,
        name,
        recruitParticipantTokens {
          token,
          id,
          maximumTests,
          createdAt,
          deletedAt,
          isUserInterviews,
        }
      }
    }
  }
`;

export const updateMaximumTests = async (projectId, segmentId, linkId, maximumTests) => {
  const query = gql`
    mutation UpdateMaximumTests($id: ID!, $projectId: ID!, $segmentId: ID!, $maximumTests: Int!) {
      updateMaximumTests(id: $id, projectId: $projectId, segmentId: $segmentId, maximumTests: $maximumTests) {
        links {
          id
          maximumTests
        }
      }
    }
  `;

  const { data } = await client.getClient().mutate({
    mutation: query,
    variables: {
      id: linkId, projectId, segmentId, maximumTests,
    },
  });

  return data;
};

export const handleMoveDraftUpdate = (cache, { data: { changeProjectStatus } }) => {
  const { project } = changeProjectStatus;

  const { approvals: cachedApprovals } = cache.readQuery({
    query: GET_APPROVALS,
  });

  const newApprovals = cachedApprovals.filter((cachedApproval) => (
    cachedApproval.project.id !== project.id
  ));

  cache.writeQuery({
    query: GET_APPROVALS,
    data: {
      approvals: newApprovals,
    },
  });
};

export const UPDATE_PARTICIPANTS_NUMBER = gql`
    mutation UpdateParticipantNumber(
      $projectId: ID!,
      $participantsNumber: Int!,
      $token: String,
      $couponCode: String,
      $rememberCard: Boolean!,
      $paymentMethodId: String,
      $segmentId: ID!,
      $isAdmin: Boolean,
    ) {
      updateParticipantsNumber(
        projectId: $projectId,
        participantsNumber: $participantsNumber,
        token: $token,
        couponCode: $couponCode,
        rememberCard: $rememberCard,
        paymentMethodId: $paymentMethodId,
        segmentId: $segmentId,
        isAdmin: $isAdmin,
      ) {
        project {
          id
          audienceParticipants
          status
        }
      }
    }
`;
